import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ArrowLeft, Calendar, Clock, MapPin, Users, CheckCircle, XCircle, Plus, PlayCircle, FileCheck, Edit, Ban, Download } from 'lucide-react';
import { AuditTrailViewer } from '../../components/AuditTrail';
import { useAlert } from '../../context/AlertContext';
import { usePermissions } from '../../context/PermissionContext';
import { useSite } from '../../context/SiteContext';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { canManageMeeting } from '../../utils/roleUtils';
import TabNavigation from '../../components/TabNavigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MeetingsDetail = ({ user, onLogout }) => {
  const { hasPermission } = usePermissions();
  const { activeSiteId } = useSite();
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useAlert();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showMinutesModal, setShowMinutesModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details' or 'audit'
  const [pdfLoading, setPdfLoading] = useState(false);
  const [editingActionItemId, setEditingActionItemId] = useState(null);
  const [actionItem, setActionItem] = useState({
    description: '',
    assigned_to: '',
    assigned_to_name: '',
    due_date: ''
  });
  const [minutes, setMinutes] = useState('');
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchMeeting();
    fetchUsers();
  }, [id, activeSiteId]);

  const fetchMeeting = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/meetings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeeting(response.data);
      setMinutes(response.data.minutes || '');
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load meeting');
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = activeSiteId ? `${API}/users?site_id=${activeSiteId}` : `${API}/users`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch users');
    }
  };

  const handleMarkAttendance = async (userId, attended) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/meetings/${id}/mark-attendance`,
        { user_id: userId, attended },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchMeeting();
    } catch (err) {
      showError('Failed to mark attendance');
    }
  };

  const getMinDueDate = () => {
    const today = new Date().toISOString().split('T')[0];
    const meetingDate = meeting?.scheduled_date
      ? new Date(meeting.scheduled_date).toISOString().split('T')[0]
      : today;
    return today > meetingDate ? today : meetingDate;
  };

  const handleAddActionItem = async () => {
    const minDate = getMinDueDate();
    if (actionItem.due_date < minDate) {
      showError('Due date cannot be before the meeting date');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const user = users.find(u => u.id === actionItem.assigned_to);

      const payload = {
        ...actionItem,
        assigned_to_name: user?.name || '',
        due_date: new Date(actionItem.due_date).toISOString(),
        status: 'Open'
      };

      await axios.post(
        `${API}/meetings/${id}/action-items`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowActionModal(false);
      setActionItem({ description: '', assigned_to: '', assigned_to_name: '', due_date: '' });
      fetchMeeting();
      showSuccess('Action item added successfully');
    } catch (err) {
      showError('Failed to add action item');
    }
  };

  const handleOpenEditActionItem = (action) => {
    setEditingActionItemId(action.id);
    setActionItem({
      description: action.description || '',
      assigned_to: action.assigned_to || '',
      assigned_to_name: action.assigned_to_name || '',
      due_date: action.due_date ? new Date(action.due_date).toISOString().split('T')[0] : ''
    });
    setShowActionModal(true);
  };

  const handleSaveActionItem = async () => {
    try {
      const token = localStorage.getItem('token');
      const user = users.find(u => u.id === actionItem.assigned_to);

      const payload = {
        description: actionItem.description,
        assigned_to: actionItem.assigned_to,
        assigned_to_name: user?.name || actionItem.assigned_to_name || '',
        due_date: new Date(actionItem.due_date).toISOString()
      };

      await axios.put(
        `${API}/meetings/${id}/action-items/${editingActionItemId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowActionModal(false);
      setEditingActionItemId(null);
      setActionItem({ description: '', assigned_to: '', assigned_to_name: '', due_date: '' });
      fetchMeeting();
      showSuccess('Action item updated successfully');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to update action item');
    }
  };

  const handleUpdateActionItemStatus = async (actionItemId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/meetings/${id}/action-items/${actionItemId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchMeeting();
      showSuccess(`Action item marked as ${newStatus}`);
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to update action item status');
    }
  };

  const getNextActionStatus = (currentStatus) => {
    if (currentStatus === 'Open') return 'In Progress';
    if (currentStatus === 'In Progress') return 'Completed';
    return null;
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/meetings/${id}/update-status`,
        { status: newStatus, minutes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchMeeting();
      setShowMinutesModal(false);
      showSuccess(`Meeting marked as ${newStatus}`);
    } catch (err) {
      showError('Failed to update meeting status');
    }
  };

  // Honour the backend's Content-Disposition filename (`<meeting_number>_
  // <safe_title>.pdf`) so circulated copies carry the meeting number in
  // their filename. Falls back to a sensible default if the header is
  // missing or unparseable.
  const handleDownloadPDF = async () => {
    try {
      setPdfLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/meetings/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const cd = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
      let filename = `${meeting?.meeting_number || 'meeting'}.pdf`;
      if (cd) {
        const match = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
        if (match) filename = match[1];
      }
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to download meeting PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Scheduled': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !meeting) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Alert variant="destructive">
          <AlertDescription>{error || 'Meeting not found'}</AlertDescription>
        </Alert>
        <Button onClick={() => navigate('/meetings')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Meetings
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/meetings')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Meetings
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{meeting.title}</h1>
              <p className="text-gray-600 mt-1">{meeting.meeting_number}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(meeting.status)}`}>
                {meeting.status}
              </span>
              {meeting.is_mandatory && (
                <span className="px-4 py-2 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                  Mandatory
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mb-6 flex gap-3">
          {/* Edit button - only shown for Scheduled meetings */}
          {meeting.status?.toLowerCase() === 'scheduled' && hasPermission('meetings', 'update') && canManageMeeting(user, meeting) && (
            <Button
              variant="outline"
              onClick={() => navigate(`/meetings/${id}/edit`)}
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Meeting
            </Button>
          )}

          {/* Start Meeting button */}
          {meeting.status?.toLowerCase() === 'scheduled' && hasPermission('meetings', 'update') && (
            <Button
              onClick={() => handleUpdateStatus('In Progress')}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              Start Meeting
            </Button>
          )}

          {/* Cancel Meeting button */}
          {meeting.status?.toLowerCase() === 'scheduled' && hasPermission('meetings', 'update') && canManageMeeting(user, meeting) && (
            <Button
              onClick={() => setShowCancelModal(true)}
              variant="outline"
              className="border-red-600 text-red-600 hover:bg-red-50"
            >
              <Ban className="w-4 h-4 mr-2" />
              Cancel Meeting
            </Button>
          )}

          {/* Complete Meeting button */}
          {meeting.status?.toLowerCase() === 'in progress' && hasPermission('meetings', 'update') && (
            <Button
              onClick={() => setShowMinutesModal(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              <FileCheck className="w-4 h-4 mr-2" />
              Complete Meeting
            </Button>
          )}

          {/* Download PDF — always available so the agenda can circulate
              before the meeting and the minutes after. */}
          <Button
            onClick={handleDownloadPDF}
            disabled={pdfLoading}
            variant="outline"
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            {pdfLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download PDF
          </Button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('details')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'audit'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Audit History
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' ? (
          <>
        {/* Meeting Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Meeting Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm font-semibold text-gray-600">Date</p>
                  <p className="text-gray-900">{formatDate(meeting.scheduled_date)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm font-semibold text-gray-600">Time</p>
                  <p className="text-gray-900">
                    {formatTime(meeting.scheduled_date)} ({meeting.duration} min)
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm font-semibold text-gray-600">Location</p>
                  <p className="text-gray-900">{meeting.location}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm font-semibold text-gray-600">Organizer</p>
                  <p className="text-gray-900">{meeting.organizer_name}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Agenda */}
        {meeting.agenda_items && meeting.agenda_items.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Agenda</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {meeting.agenda_items.map((item, index) => (
                  <div key={item.id || `agenda-${index}`} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{index + 1}. {item.topic}</h4>
                        {item.presenter && (
                          <p className="text-sm text-gray-600 mt-1">Presenter: {item.presenter}</p>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">{item.duration} min</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Attendees */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Attendees ({meeting.attendees?.length || 0})</CardTitle>
          </CardHeader>
          <CardContent>
            {meeting.attendees && meeting.attendees.length > 0 ? (
              <div className="space-y-2">
                {meeting.attendees.map((attendee, index) => (
                  <div key={attendee.id || attendee.user_id || `attendee-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {attendee.attended ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-400" />
                      )}
                      <div>
                        <p className="font-semibold text-gray-900">{attendee.user_name}</p>
                        <p className="text-sm text-gray-600">{attendee.role}</p>
                      </div>
                    </div>
                    {meeting.status?.toLowerCase() === 'in progress' && (
                      <Button
                        size="sm"
                        onClick={() => handleMarkAttendance(attendee.user_id, !attendee.attended)}
                        className={attendee.attended ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
                      >
                        {attendee.attended ? 'Mark Absent' : 'Mark Present'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No attendees added</p>
            )}
          </CardContent>
        </Card>

        {/* Action Items */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Action Items</CardTitle>
              {meeting.status?.toLowerCase() === 'in progress' && (
                <Button
                  size="sm"
                  onClick={() => { setEditingActionItemId(null); setActionItem({ description: '', assigned_to: '', assigned_to_name: '', due_date: '' }); setShowActionModal(true); }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Action
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {meeting.action_items && meeting.action_items.length > 0 ? (
              <div className="space-y-3">
                {meeting.action_items.map((action, index) => (
                  <div key={action.id || `action-${index}`} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{index + 1}. {action.description}</p>
                        <div className="mt-2 text-sm text-gray-600">
                          <p>Assigned to: <span className="font-semibold">{action.assigned_to_name}</span></p>
                          <p>Due: {formatDate(action.due_date)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {(meeting.status?.toLowerCase() === 'in progress' || meeting.status?.toLowerCase() === 'completed') && action.status !== 'Completed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenEditActionItem(action)}
                            className="text-xs"
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                        )}
                        {(meeting.status?.toLowerCase() === 'in progress' || meeting.status?.toLowerCase() === 'completed') && getNextActionStatus(action.status) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateActionItemStatus(action.id, getNextActionStatus(action.status))}
                            className="text-xs"
                          >
                            {getNextActionStatus(action.status) === 'In Progress' ? 'Start' : 'Complete'}
                          </Button>
                        )}
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          action.status === 'Completed' ? 'bg-green-100 text-green-800' :
                          action.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {action.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No action items yet</p>
            )}
          </CardContent>
        </Card>

        {/* Meeting Minutes */}
        {meeting.minutes && (
          <Card>
            <CardHeader>
              <CardTitle>Meeting Minutes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">{meeting.minutes}</p>
              </div>
            </CardContent>
          </Card>
        )}
          </>
        ) : (
          /* Audit History Tab */
          <Card>
            <CardContent className="pt-6">
              <AuditTrailViewer
                entityType="meeting"
                entityId={id}
                title="Meeting Audit History"
              />
            </CardContent>
          </Card>
        )}

        {/* Action Item Modal */}
        {showActionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{editingActionItemId ? 'Edit Action Item' : 'Add Action Item'}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={actionItem.description}
                      onChange={(e) => setActionItem({ ...actionItem, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      rows="3"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Assign To <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={actionItem.assigned_to}
                      onChange={(e) => setActionItem({ ...actionItem, assigned_to: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      required
                    >
                      <option value="">Select user...</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>{user.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Due Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={actionItem.due_date}
                      onChange={(e) => setActionItem({ ...actionItem, due_date: e.target.value })}
                      min={getMinDueDate()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => { setShowActionModal(false); setEditingActionItemId(null); setActionItem({ description: '', assigned_to: '', assigned_to_name: '', due_date: '' }); }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={editingActionItemId ? handleSaveActionItem : handleAddActionItem}
                    disabled={!actionItem.description || !actionItem.assigned_to || !actionItem.due_date || actionItem.due_date < getMinDueDate()}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    {editingActionItemId ? 'Save Changes' : 'Add Action'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Minutes Modal */}
        {showMinutesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Complete Meeting</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Meeting Minutes/Summary
                    </label>
                    <textarea
                      value={minutes}
                      onChange={(e) => setMinutes(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      rows="8"
                      placeholder="Enter meeting minutes, key discussions, and decisions made..."
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setShowMinutesModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleUpdateStatus('Completed')}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    Complete Meeting
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Meeting Confirmation Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <Ban className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Cancel Meeting</h3>
                </div>
                <p className="text-gray-600 mb-2">
                  Are you sure you want to cancel <span className="font-semibold">"{meeting.title}"</span>?
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  All attendees will be notified about the cancellation. This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowCancelModal(false)}
                    className="flex-1"
                  >
                    Go Back
                  </Button>
                  <Button
                    onClick={async () => {
                      setShowCancelModal(false);
                      await handleUpdateStatus('Cancelled');
                    }}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                  >
                    Cancel Meeting
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default MeetingsDetail;
